import React from 'react';
import { Card, Typography, Skeleton, Row, Col } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const { Text } = Typography;

const STATUS_COLORS = {
  PAID: '#52c41a',
  SENT: '#1890ff',
  PARTIAL: '#faad14',
  DRAFT: '#d9d9d9',
};

const InvoiceStatusCard = ({ data, loading }) => {
  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value?.toLocaleString('en-IN') || 0}`;
  };

  if (loading) {
    return (
      <Card size="small" title={<Text strong>Invoice Status</Text>}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  // Process invoice data by status
  const invoicesByStatus = data?.by_status || [];

  const pieData = invoicesByStatus.map(item => ({
    name: item.status,
    value: item.count,
    amount: item.amount,
    fill: STATUS_COLORS[item.status] || '#d9d9d9'
  })).filter(item => item.value > 0);

  const totalInvoices = pieData.reduce((sum, item) => sum + item.value, 0);
  const totalAmount = pieData.reduce((sum, item) => sum + (item.amount || 0), 0);

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null; // Don't show label for small slices
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={10}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Card size="small" title={<Text strong>Invoice Status</Text>}>
      {pieData.length > 0 ? (
        <>
          <Row gutter={[8, 8]} style={{ marginBottom: '8px' }}>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: '11px' }}>Total: </Text>
              <Text strong style={{ fontSize: '13px' }}>{totalInvoices}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: '11px' }}>Value: </Text>
              <Text strong style={{ fontSize: '13px' }}>{formatCurrency(totalAmount)}</Text>
            </Col>
          </Row>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
                labelLine={false}
                label={renderCustomLabel}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name, props) => [
                  `${value} invoices (${formatCurrency(props.payload.amount)})`,
                  name
                ]}
                contentStyle={{ fontSize: '12px' }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconSize={10}
                formatter={(value, entry) => (
                  <span style={{ fontSize: '11px', color: '#595959' }}>
                    {value}: {entry.payload.value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </>
      ) : (
        <div style={{
          height: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Text type="secondary">No invoice data available</Text>
        </div>
      )}
    </Card>
  );
};

export default InvoiceStatusCard;
